import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #isMobile() {
    const breakpoint = this.dataset.breakpoint || '750';
    return !window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  }

  #controller = new AbortController();
  #observer = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#checkContentVisibility();
    this.#setupMutationObserver();

    const details = this.querySelector('details');
    const summary = details ? details.querySelector('summary') : null;

    if (summary) {
      this.#setDefaultOpenState();

      this.addEventListener('keydown', this.#handleKeyDown, { signal });
      summary.addEventListener('click', this.handleClick, { signal });

      const breakpoint = this.dataset.breakpoint || '750';
      const mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
      mediaQuery.addEventListener('change', this.#handleMediaQueryChange, { signal });
    }

    // After all siblings have connected and visibility is resolved, ensure the
    // first visible accordion in a .accordion group is open when none are open.
    this.#scheduleFirstAccordionCheck();
  }

  /**
   * Schedules a deferred check on the parent .accordion container.
   * Uses a per-container pending flag so only one check fires per batch of connects.
   */
  #scheduleFirstAccordionCheck() {
    const container = this.closest('.accordion');
    if (!container) return;

    // Mark that a check is already pending for this container so we don't
    // queue multiple setTimeout calls for the same group.
    if (container._firstAccordionCheckPending) return;
    container._firstAccordionCheckPending = true;

    setTimeout(() => {
      container._firstAccordionCheckPending = false;
      AccordionCustom.#openFirstIfNoneOpen(container);
    }, 0);
  }

  /**
   * If no visible accordion-custom in `container` has its details open,
   * opens the first visible one.
   * @param {Element} container - The .accordion wrapper element.
   */
  static #openFirstIfNoneOpen(container) {
    const rows = Array.from(container.querySelectorAll(':scope > accordion-custom'));
    const visibleRows = rows.filter((row) => !row.hasAttribute('hidden') && row.style.display !== 'none');

    const anyOpen = visibleRows.some((row) => {
      const det = row.querySelector('details');
      return det && det.open;
    });

    if (!anyOpen && visibleRows.length > 0) {
      const firstDetails = visibleRows[0].querySelector('details');
      if (firstDetails) firstDetails.open = true;
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  #checkContentVisibility() {
    if (window.Shopify && window.Shopify.designMode) return;

    const content = this.querySelector('.details-content');
    if (!content) return;

    const clone = content.cloneNode(true);
    clone.querySelectorAll('script, style, template').forEach((el) => el.remove());

    const text = clone.textContent.replace(/\s+/g, '').trim();
    const hasMedia = clone.querySelector('img, video, iframe, svg, canvas, input, select, textarea, button, audio, picture') !== null;

    if (text === '' && !hasMedia) {
      this.style.display = 'none';
      this.setAttribute('hidden', '');
    } else {
      this.style.display = '';
      this.removeAttribute('hidden');
    }
  }

  #setupMutationObserver() {
    const content = this.querySelector('.details-content');
    if (!content) return;

    this.#observer = new MutationObserver(() => {
      this.#checkContentVisibility();
    });
    this.#observer.observe(content, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = this.#isMobile();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = this.#isMobile();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
